import { urlEscaped } from '@app/utils/urlEscaped'

import { createApiAction } from '@app/packages/api/utils'
import type {
  ApiAnnouncement,
  ApiAnnouncementResponse,
  ApiAvatar,
  ApiLocation,
  ApiMapAnnouncement,
  ApiMapLocation,
  ApiUser,
} from '@app/packages/api/v2/types/entities'
import type { ApiDataResponse, ListResponseMeta } from '@app/packages/api/v2/types/misc'
import type { AnnouncementCreateRequest, AnnouncementQueryRequest, AnnouncementSearchQuery } from '@app/packages/api/v2/types/params'
import { v2Path } from '@app/packages/api/v2/utils'

type AnnouncementsResponse = ApiDataResponse<ApiAnnouncement[]> & { included?: (ApiUser | ApiLocation | ApiAvatar | ApiAnnouncementResponse)[] }

/** A cursor continues a search; a query starts one. */
type SearchRequest = { cursor: string } | AnnouncementSearchQuery

export const getAnnouncementsSearch = (request: SearchRequest) =>
  createApiAction('getAnnouncementsSearch', dispatch =>
    dispatch<AnnouncementsResponse & { meta: ListResponseMeta & { cursor: string } }>({
      method: 'GET',
      endpoint: v2Path('/api/v2/announcements/search', request),
    })
  )

/**
 * The same search trimmed to what the map needs. Both types are renamed, because a response
 * carrying two attributes of a location must not pass for the whole entity in the store.
 */
export const getAnnouncementsSearchMap = (query: SearchRequest) =>
  createApiAction('getAnnouncementsSearchMap', async dispatch => {
    const resp = await dispatch<{
      data: (Omit<ApiMapAnnouncement, 'type'> & { type: 'announcements' })[]
      included?: (Omit<ApiMapLocation, 'type'> & { type: 'locations' })[]
      meta: ListResponseMeta & { cursor: string }
    }>({
      method: 'GET',
      endpoint: v2Path('/api/v2/announcements/search', {
        ...query,
        order: undefined,
        'fields[announcements]': 'location',
        'fields[locations]': 'latitude,longitude',
        include: 'location',
      }),
    })

    return {
      ...resp,
      data: resp.data.map(announcement => ({ ...announcement, type: 'map_announcements' as const })),
      included: (resp.included ?? []).map(location => ({ ...location, type: 'map_locations' as const })),
    }
  })

export const getAnnouncementsSearchCount = (params: Omit<AnnouncementSearchQuery, 'page' | 'per_page'>) =>
  createApiAction('getAnnouncementsSearchCount', dispatch =>
    dispatch<{ meta: { total: number } }>({ method: 'GET', endpoint: v2Path('/api/v2/announcements/search', { ...params, page: 1, per_page: 1 }) })
  )

export const getAnnouncementsUpcoming = () =>
  createApiAction('getAnnouncementsUpcoming', dispatch => dispatch<AnnouncementsResponse>({ method: 'GET', endpoint: '/api/v2/announcements/upcoming' }))

export const getAnnouncementsByQuery = (request: AnnouncementQueryRequest) =>
  createApiAction('getAnnouncementsByQuery', dispatch => dispatch<AnnouncementsResponse>({ method: 'GET', endpoint: v2Path('/api/v2/announcements', request) }))

export const getAnnouncementsById = (id: string) =>
  createApiAction('getAnnouncementsById', dispatch =>
    dispatch<ApiDataResponse<ApiAnnouncement> & { included?: (ApiUser | ApiLocation | ApiAvatar | ApiAnnouncementResponse)[] }>({
      method: 'GET',
      endpoint: urlEscaped`/api/v2/announcements/${id}`,
    })
  )

export const postAnnouncements = (request: AnnouncementCreateRequest) =>
  createApiAction('postAnnouncements', dispatch =>
    dispatch<ApiDataResponse<ApiAnnouncement>>({ method: 'POST', endpoint: '/api/v2/announcements', body: JSON.stringify(request) })
  )

export const putAnnouncementsById = (announcement_id: string, request: AnnouncementCreateRequest) =>
  createApiAction('putAnnouncementsById', dispatch =>
    dispatch<ApiDataResponse<ApiAnnouncement>>({
      method: 'PUT',
      endpoint: urlEscaped`/api/v2/announcements/${announcement_id}`,
      body: JSON.stringify(request),
    })
  )

export const putAnnouncementsByIdCancel = (id: string) =>
  createApiAction('putAnnouncementsByIdCancel', dispatch =>
    dispatch<ApiDataResponse<ApiAnnouncement>>({ method: 'PUT', endpoint: urlEscaped`/api/v2/announcements/${id}/cancel` })
  )

export const putAnnouncementsByIdHide = (id: string) =>
  createApiAction('putAnnouncementsByIdHide', dispatch => dispatch<unknown>({ method: 'PUT', endpoint: urlEscaped`/api/v2/announcements/${id}/hide` }))
